import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Power, PowerOff } from "lucide-react";

interface IntegrationToggleProps {
  enabled: boolean;
  canWrite: boolean;
  isEnabling: boolean;
  isDisabling: boolean;
  onEnable: () => void;
  onDisable: () => void;
}

export const IntegrationToggle = ({
  enabled,
  canWrite,
  isEnabling,
  isDisabling,
  onEnable,
  onDisable,
}: IntegrationToggleProps) => {
  const { t } = useTranslation();
  const [disableOpen, setDisableOpen] = React.useState(false);

  if (enabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Power className="h-5 w-5 text-green-600" />
            {t("pms.toggle.disable")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
            <DialogTrigger asChild>
              <Button
                variant="new_destructive"
                disabled={!canWrite || isDisabling}
              >
                <PowerOff />
                {t("pms.toggle.disable")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("pms.toggle.disableConfirmTitle")}</DialogTitle>
                <DialogDescription>
                  {t("pms.toggle.disableConfirmDescription")}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="new_outline"
                  onClick={() => setDisableOpen(false)}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  variant="new_destructive"
                  disabled={!canWrite || isDisabling}
                  onClick={() => {
                    onDisable();
                    setDisableOpen(false);
                  }}
                >
                  {t("pms.toggle.disable")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PowerOff className="text-muted-foreground h-5 w-5" />
          {t("pms.toggle.enable")}
        </CardTitle>
        <CardDescription>{t("pms.toggle.enableDescription")}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          variant="new_default"
          disabled={!canWrite || isEnabling}
          onClick={onEnable}
        >
          <Power />
          {t("pms.toggle.enable")}
        </Button>
      </CardContent>
    </Card>
  );
};
